import {
  FindDocumentsToolName,
  GetDocumentOverviewToolName,
} from './documentDiscoveryContract';
import { DocumentSearchToolName } from './documentSearchContract';
import { RecordAnswerSourcesToolName } from './evidenceContract';
import { FetchUrlToolName } from './fetchUrlContract';
import type { BuiltinToolName } from './index';
import { SearchPdfToolName } from './searchPdfContract';
import { SeeImageToolName } from './seeImageContract';
import { ShopProductToolNames } from './shopAssistant/shopProductToolsContract';
import { ViewPageToolName } from './viewPageContract';
import { EndCallToolName, TransferCallToolName } from './voiceToolContract';
import { WebSearchToolName } from './webSearchContract';
import { UpdateWorkingMemoryToolName } from './workingMemoryContract';

/**
 * Admin-facing metadata for a built-in tool. This is intentionally separate
 * from the LLM-facing tool description on the tool itself — it describes the
 * capability for the read-only "Built-in" catalog in the Agent Tools hub.
 */
export interface BuiltinToolCatalogEntry {
  name: BuiltinToolName;
  displayName: string;
  description: string;
}

/**
 * Read-only catalog of built-in tools shipped and maintained by Kanbu. Static
 * by design: the frontend imports it directly, no API round-trip needed.
 */
export const builtinToolCatalog: BuiltinToolCatalogEntry[] = [
  {
    name: SearchPdfToolName,
    displayName: 'Search PDF URL',
    description:
      'Searches embedded text in a web PDF and returns passages with page numbers. Knowledge-base documents use Document Search.',
  },
  {
    name: ViewPageToolName,
    displayName: 'View PDF Page',
    description:
      'Inspects a PDF page or figure crop from an authorized knowledge-base document or allowed URL. Shares the five-image turn limit with See Image.',
  },
  {
    name: SeeImageToolName,
    displayName: 'See Image',
    description:
      'Inspects an image visually to answer questions about diagrams, charts and photographs. Uses the same allowed domains as Fetch URL.',
  },
  {
    name: RecordAnswerSourcesToolName,
    displayName: 'Voice: Record answer sources',
    description:
      'Optional voice attribution metadata. Adds a model tool call and may increase response latency; disabled by default.',
  },
  {
    name: DocumentSearchToolName,
    displayName: 'Document Search',
    description:
      'Searches the agent knowledge base (RAG) to answer questions from uploaded documents and synced content.',
  },
  {
    name: FindDocumentsToolName,
    displayName: 'Find Documents',
    description:
      'Answers "what do you have about X" at the level of whole documents — titles, summaries and section outlines, no passages. Worth enabling on a large knowledge base, where the agent can pick the right document first and then search inside it; on a handful of documents it only adds a round-trip.',
  },
  {
    name: GetDocumentOverviewToolName,
    displayName: 'Document Overview',
    description:
      "Reads one document's full section outline and its long summary, for questions about what a document covers rather than what it says on a point. Requires Document Search alongside it to quote anything specific.",
  },
  {
    name: WebSearchToolName,
    displayName: 'Web Search',
    description:
      'Searches the web, restricted to the allowed domains configured for the agent, for up-to-date external information.',
  },
  {
    name: ShopProductToolNames.GetOperator,
    displayName: 'Operator Handoff',
    description:
      'Escalates the conversation to a human operator when the agent cannot resolve the request.',
  },
  {
    name: ShopProductToolNames.GetShopProducts,
    displayName: 'Shop: Search Products',
    description:
      'Searches the product catalog by query to recommend and present matching products.',
  },
  {
    name: ShopProductToolNames.GetSimilarProducts,
    displayName: 'Shop: Similar Products',
    description:
      'Finds the products closest to a given one in the catalog — alternatives to browse, or a replacement when the chosen item is unavailable.',
  },
  {
    name: ShopProductToolNames.GetShopProductDetails,
    displayName: 'Shop: Product Details',
    description:
      'Loads the full record of up to four known products — by internal ID, or by external ID, EAN or ISBN — including description, parameters, images, gifts, accessories and availability. Also used to compare products.',
  },
  {
    name: ShopProductToolNames.GetShopCategories,
    displayName: 'Shop: Categories',
    description: 'Lists shop categories to help users browse the catalog.',
  },
  {
    name: ShopProductToolNames.GetShopDeliveryOptions,
    displayName: 'Shop: Delivery Options',
    description: 'Returns available delivery and shipping options.',
  },
  {
    name: ShopProductToolNames.RecommendShopProducts,
    displayName: 'Shop: Recommend Products',
    description:
      'Presents agent-selected products to the user as product cards. Card data is loaded from the catalog by product ID, so the agent cannot invent products or links.',
  },
  {
    name: ShopProductToolNames.ShowShopProductDetail,
    displayName: 'Shop: Show Product Detail',
    description:
      'Presents the full detail card of one product (images, parameters, categories) to the user. Data is loaded from the catalog by product ID.',
  },
  {
    name: UpdateWorkingMemoryToolName,
    displayName: 'Working Memory',
    description:
      'Lets the agent keep a short per-conversation scratchpad of facts, constraints and open questions, so details established in earlier turns survive instead of being re-invented.',
  },
  {
    name: TransferCallToolName,
    displayName: 'Transfer Call',
    description:
      'Hands a live phone call over to a human operator (cold transfer). Voice calls only — the agent drops off once the caller is connected.',
  },
  {
    name: EndCallToolName,
    displayName: 'End Call',
    description:
      'Ends a live phone call once the conversation is genuinely complete, instead of leaving the line open until the caller hangs up.',
  },
  {
    name: FetchUrlToolName,
    displayName: 'Fetch URL',
    description:
      'Reads the content of a specific web page or online document — PDF, Word, PowerPoint, images and spreadsheets are converted to text (long documents are read in pages) — and verifies that links resolve before sharing them.',
  },
];
